<template>
    <div>
        <app-dlg
                :show-dlg.sync="shown"
                :title="`${user.name} Settings`"
                :dlg-width="600"
                :show-loading="showLoading"
                :default-padding="false"
        >
            <template v-slot:default>

                <app-divider label="User Options"></app-divider>

                <v-form ref="modifyForm" @submit="$event.preventDefault()">
                    <div class="d-flex flex-column px-6">
                        <div class="pt-3 d-flex align-center justify-center">
                            <v-text-field
                                    color="primary"
                                    v-model="editUser.name"
                                    label="Account Name"
                                    outlined
                                    dense
                                    :rules="[validationRules.required]"
                                    hide-details="auto"
                                    @input="editUserChangesMade = true"
                            >
                            </v-text-field>
                        </div>
                        <div class="pt-4 d-flex align-center justify-center">
                            <v-text-field
                                    color="primary"
                                    outlined
                                    v-model="editUser.username"
                                    dense
                                    label="Username"
                                    placeholder="eg. person@domain.com"
                                    hide-details="auto"
                                    :hint="editUserUsernameErrors"
                                    :error-messages="editUserUsernameErrors !== null ? [editUserUsernameErrors] : null"
                                    @input="editUserChangesMade = true; editUserUsernameErrors = null"
                                    :rules="[validationRules.required]"
                            >
                            </v-text-field>
                        </div>

                        <div class="pt-4 d-flex align-center justify-center">
                            <v-text-field
                                    ref="password"
                                    color="primary"
                                    outlined
                                    v-model="editUser.password"
                                    dense
                                    label="Password"
                                    type="password"
                                    placeholder="Password"
                                    hide-details="auto"
                                    autocomplete="new-password"
                                    :rules="editUser.password ? [validationRules.required, validationRules.upperCase, validationRules.lowerCase, validationRules.numeric, validationRules.specialCharacter, validationRules.min6Characters] : []"
                                    @input="editUserChangesMade = true"
                                    class="flex-grow-1"
                            >
                            </v-text-field>
                            <v-btn
                                    outlined
                                    small
                                    color="blue"
                                    class="my-1 ml-2"
                                    :loading="sendingPasswordReset"
                                    :disabled="sendingPasswordReset"
                                    @click="sendPasswordReset"
                            >
                                <v-icon left>mdi-send-lock</v-icon>
                                Send Reset Link
                            </v-btn>
                        </div>

                        <div class="pt-2 d-flex align-center justify-space-between">
                            <div class="d-flex align-center flex-grow-1">
                                <v-icon :color="user.totp.enabled ? null : 'error'">
                                    {{ user.totp.enabled ? 'mdi-qrcode' : 'mdi-qrcode-remove' }}
                                </v-icon>
                                <div class="body-1 px-3 py-2"
                                     :class="user.totp.enabled ? null : 'error--text'">
                                    TOTP Login {{ user.totp.enabled ? 'Enabled' : 'Disabled' }}
                                </div>
                            </div>

                            <v-btn
                                    outlined
                                    small
                                    color="blue"
                                    :loading="resettingTotp"
                                    :disabled="resettingTotp"
                                    @click="resetTotp"
                                    class="mr-2"
                                    v-if="user.totp.enabled"
                            >
                                <v-icon left>mdi-lock-reset</v-icon>
                                Reset TOTP
                            </v-btn>

                            <v-btn
                                    outlined
                                    small
                                    :color="user.totp.enabled ? 'error' : 'green'"
                                    :loading="enablingTOTP"
                                    :disabled="enablingTOTP"
                                    @click="enableTOTP"
                            >
                                <v-icon left>
                                    {{ !user.totp.enabled ? 'mdi-qrcode' : 'mdi-qrcode-remove' }}
                                </v-icon>
                                {{ !user.totp.enabled ? 'Enable' : 'Disable' }}
                            </v-btn>
                        </div>
                    </div>

                    <div class="px-3 pt-4 d-flex justify-end">
                        <v-btn
                                ref="passwordBtn"
                                :disabled="showLoading"
                                color="primary"
                                @click="resetValue"
                                small
                                outlined
                                class="mr-2"
                        >
                            Cancel
                        </v-btn>

                        <v-btn
                                ref="passwordBtn"
                                :disabled="!editUserChangesMade || showLoading"
                                :loading="showLoading"
                                color="primary"
                                @click="saveChanges"
                                small
                        >
                            Save Changes
                        </v-btn>
                    </div>
                </v-form>

                <app-divider label="Sites"></app-divider>

                <div>
                    <v-data-table
                            :headers="siteColumns"
                            :items="viewSites"
                            :search="$store.state.ref.dashboard.filterStr"
                            :items-per-page="viewSites.length"
                            hide-default-footer
                            fixed-header
                            sortBy="name"
                    >
                        <template v-slot:item.name="{ item }">
                            <div class="d-flex align-center overline" style="white-space: nowrap">
                                <v-icon left small>mdi-domain</v-icon>
                                {{ item.name }}
                            </div>
                        </template>

                        <template v-slot:item.default="{ item }">
                            <v-tooltip top :disabled="showLoading">
                                <template v-slot:activator="{on}">
                                    <v-btn
                                            :disabled="showLoading"
                                            :color="user.defaultSite === item._id ? 'blue' : 'grey darken-4'"
                                            class="mx-2"
                                            small
                                            icon
                                            v-on="on"
                                            @click="setDefaultSite(user.defaultSite === item._id ? null : item._id)"
                                    >
                                        <v-icon>{{ user.defaultSite === item._id ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}</v-icon>
                                    </v-btn>
                                </template>
                                {{ user.defaultSite === item._id ? 'Uncheck to clear' : 'Mark as default site' }}
                            </v-tooltip>
                        </template>

                        <template v-slot:item.admin="{ item }">
                            <v-tooltip top :disabled="showLoading">
                                <template v-slot:activator="{on}">
                                    <v-btn
                                            :disabled="showLoading"
                                            :color="user.adminSiteId === item._id ? 'primary' : 'grey darken-4'"
                                            class="mx-2"
                                            small
                                            icon
                                            v-on="on"
                                            @click="setAdminSite(user.adminSiteId === item._id ? null : item._id)"
                                    >
                                        <v-icon>{{ user.adminSiteId === item._id ? 'mdi-shield-check' : 'mdi-shield-outline' }}</v-icon>
                                    </v-btn>
                                </template>
                                {{ user.adminSiteId === item._id ? 'Admin Site' : 'Mark as Admin site' }}
                            </v-tooltip>
                        </template>

                        <template v-slot:item.remove="{ item }">
                            <v-tooltip top :disabled="showLoading">
                                <template v-slot:activator="{on}">
                                    <div style="display: inline-block" v-on="on">
                                        <v-btn
                                                :disabled="showLoading || site._id === item._id || viewSites.length <= 1 || user.adminSiteId === item._id || user.defaultSite === item._id"
                                                color="error"
                                                small
                                                icon
                                                @click="removeSiteStart(item)"
                                        >
                                            <v-icon>mdi-delete</v-icon>
                                        </v-btn>
                                    </div>
                                </template>
                                <template v-if="viewSites.length <= 1">
                                    Can't remove only site
                                </template>
                                <template v-else-if="user.adminSiteId === item._id">
                                    Can't remove Admin site
                                </template>
                                <template v-else-if="user.defaultSite === item._id">
                                    Can't remove Default site
                                </template>
                                <template v-else-if="site._id === item._id">
                                    Can't remove site (Switch to another site to remove)
                                </template>
                                <template v-else>
                                    Remove Site
                                </template>
                            </v-tooltip>
                        </template>

                    </v-data-table>

                </div>


<!--                Name-->
<!--                Username-->
<!--                password-->
<!--                totp Options-->
<!--                defaultSite-->
<!--                adminSite-->
<!--                sites (Remove)-->
            </template>
            <template v-slot:actions>
                <v-btn
                        @click="shown = false"
                        color="primary"
                        small
                        outlined
                >
                    Done
                </v-btn>
            </template>
        </app-dlg>

        <app-dlg
                v-if="removeSite"
                :show-dlg="!!removeSite"
                title="Remove Site"
        >
            <div class="amber--text">
                <div class="text-overline text-center pb-6">
                    <v-icon left color="amber">mdi-alert</v-icon>WARNING
                </div>
                <div class="text-center pb-4">
                    You are about to remove the site <strong>"{{removeSite.name}}"</strong>.
                </div>
                <div class="text-center pb-2">
                    Please note that this action <strong>can not be undone</strong>.
                </div>
            </div>
            <template v-slot:actions>

                <v-btn
                        @click="removeSite = null"
                        color="primary"
                        small
                        outlined
                        :disabled="removingSite"
                >
                    Cancel
                </v-btn>

                <v-btn
                        class="ml-2"
                        @click="removeUserSite"
                        color="primary"
                        small
                        :disabled="removingSite"
                        :loading="removingSite"
                >
                    Remove Site
                </v-btn>
            </template>
        </app-dlg>
    </div>
</template>

<script>
import {mapGetters} from "vuex";
import AppDlg from "@/components/Shared/AppDlg";
import {User} from "@/models/user";
import {arrSplice, nameSort, validationRules} from '@/core/codeFunctions'
import AppDivider from "@/components/Shared/AppDivider/AppDivider";

export default {
    components: {AppDivider, AppDlg},
    props: {
        value: Boolean
    },
    data() {
        return {
            showLoading: false,

            editUser: new User({}),
            validationRules: validationRules,

            editUserChangesMade: false,
            editUserUsernameErrors: null,

            sendingPasswordReset: false,
            resettingTotp: false,
            enablingTOTP: false,
            disablingUser: false,

            siteColumns: [
                {text: 'Site Name', value: 'name'},
                {text: 'Default Site', value: 'default', sortable: false, align: 'center'},
                {text: 'Admin Site', value: 'admin', sortable: false, align: 'center'},
                {text: 'Remove Site', value: 'remove', sortable: false, align: 'center'}
            ],

            removeSite: null,
            removingSite: false
        }
    },
    watch: {
        shown: {
            immediate: true,
            handler(val) {
                if (val)
                    this.editUser = new User(JSON.parse(JSON.stringify(this.user)))
            }
        }
    },
    methods: {

        async removeUserSite() {
            try {
                const updateKeys = ['siteIds'];
                arrSplice(this.editUser.siteIds, siteId => siteId === this.removeSite._id)
                this.removingSite = true;
                this.user.once('update', (payload) => {
                    this.removingSite = false;
                    this.removeSite = null;
                })
                await this.editUser.update(...updateKeys)
            } catch (e) {
                console.log(e)
                this.removingSite = false;
            }
        },

        removeSiteStart(siteId) {
            this.removeSite = siteId;
            this.removingSite = false;
        },


        async setAdminSite(site) {
            try {
                const updateKeys = ['adminSiteId'];
                this.editUser.adminSiteId = site;
                this.showLoading = true;
                this.user.once('update', (payload) => {
                    this.showLoading = false;
                })
                await this.editUser.update(...updateKeys)
            } catch (e) {
                console.log(e)
                this.showLoading = false;
            }
        },

        async setDefaultSite(site) {
            try {
                const updateKeys = ['defaultSite'];
                this.editUser.defaultSite = site;
                this.showLoading = true;
                this.user.once('update', (payload) => {
                    this.showLoading = false;
                })
                await this.editUser.update(...updateKeys)
            } catch (e) {
                console.log(e)
                this.showLoading = false;
            }
        },

        async saveChanges() {
            try {

                if (!this.$refs.modifyForm.validate())
                    return this.$snack.error('Please Complete All Fields')

                // Check Username Duplicate
                const {duplicates} = await this.$newReq('POST', 'users/checkUsernameModify', {
                    _id: this.editUser._id,
                    username: this.editUser.username
                })
                if (duplicates)
                    this.usernameErrors = 'Username Already Taken'

                // Build The Update Object
                const updateKeys = ['name', 'username', 'totp'];
                if (this.editUser.password)
                    updateKeys.push("password")

                this.showLoading = true;
                this.user.once('update', (payload) => {
                    this.showLoading = false;
                })

                await this.editUser.update(...updateKeys)
            } catch (e) {
                console.log(e)
                this.showLoading = false;
            }
        },

        resetValue() {
            this.editUser = new User(JSON.parse(JSON.stringify(this.user)));
            if (this.$refs.modifyForm)
                this.$refs.modifyForm.resetValidation();
            this.editUserUsernameErrors = null
            this.showLoading = false;
            this.editUserChangesMade = false;
            this.resettingTotp = false;
            this.sendingPasswordReset = false;
        },

        async sendPasswordReset() {
            try {
                this.sendingPasswordReset = true;
                const res = await this.$newReq('POST', 'users/passwordResetInvite', {_id: this.user._id})
                this.sendingPasswordReset = false;
                this.$snack.info('Password reset invite sent')
            } catch (e) {
                console.log(e)
                this.sendingPasswordReset = false;
            }
        },
        async resetTotp() {
            try {
                this.resettingTotp = true;
                const res = await this.$newReq('POST', 'users/resetTotp', {_id: this.user._id})
                this.resettingTotp = false;
                this.$snack.info('TOTP reset')
            } catch (e) {
                console.log(e)
                this.resettingTotp = false;
            }
        },

        async enableTOTP() {
            try {
                // Build The Update Object
                const updateKeys = ['totp'];
                this.editUser.totp.enabled = !this.editUser.totp.enabled

                this.enablingTOTP = true;
                this.user.once('update', (payload) => {
                    this.enablingTOTP = false;
                })

                await this.editUser.update(...updateKeys)
            } catch (e) {
                console.log(e)
                this.enablingTOTP = false;
            }
        },
    },
    computed: {
        ...mapGetters(['user', 'sites', 'site']),
        shown: {
            get() {
                return this.$store.state.userOptionsShown
            },
            set(val) {
                this.$store.state.userOptionsShown = val
            }
        },
        viewSites() {
            return this.sites.filter((site) => !site.disabled && this.user.siteIds.includes(site._id)).sort(nameSort)
        }

    },
}
</script>

<style scoped>

</style>